import React from "react";
import { useI18n } from "../../i18n/I18nContext";
import { C } from "../../constants/design";
import { LeagueMember, FieldAsset } from "../../types/game";
import {
  LEAGUE_TIERS,
  LeagueTier,
  MATCHES_PER_WEEK,
} from "../../constants/league";
import { ScheduleEntry } from "../../hooks/useLeague";
import { ComboActivation } from "../../engine/shopEngine";
import { TeamStanding } from "../../engine/leagueEngine";
import { CARD_BY_ID, FAMILIES, primaryFamily } from "../../constants/cards";
import { ComboRecap } from "./CardVisuals";
import { LeagueTeamDetail } from "./LeagueTeamDetail";

const TEAM_NAME = "Hyundai FC";

// Ligne « squelette » affichée tant que les données ne sont pas chargées
// (évite de montrer des chiffres faux pendant le chargement initial).
function SkeletonRow({ last }: { last?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 14px",
        borderBottom: last ? "none" : "1px solid rgba(255,255,255,0.04)",
      }}
    >
      <span
        style={{
          width: 18,
          height: 10,
          borderRadius: 3,
          background: "rgba(255,255,255,0.06)",
        }}
      />
      <span
        style={{
          flex: 1,
          height: 10,
          borderRadius: 3,
          background: "rgba(255,255,255,0.06)",
        }}
      />
      <span
        style={{
          width: 60,
          height: 10,
          borderRadius: 3,
          background: "rgba(255,255,255,0.05)",
        }}
      />
    </div>
  );
}

// ── ELO modal ────────────────────────────────────────────────────────────
function EloModal({
  currentKey,
  onClose,
}: {
  currentKey: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          borderRadius: 22,
          border: "1.5px solid rgba(255,255,255,0.08)",
          width: "100%",
          maxWidth: 380,
          padding: "22px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 900 }}>{t("leagueElo.title")}</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: -6 }}>
          {t("leagueElo.subtitle")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {LEAGUE_TIERS.map((tier) => {
            const active = tier.key === currentKey;
            return (
              <div
                key={tier.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: active ? `${tier.color}1A` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${active ? tier.color : "rgba(255,255,255,0.06)"}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <span style={{ fontSize: 20 }}>{tier.emoji}</span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: 800,
                    color: active ? tier.color : C.text,
                  }}
                >
                  {tier.label}
                  {active && (
                    <span
                      style={{
                        fontSize: 10,
                        marginLeft: 8,
                        background: `${tier.color}22`,
                        color: tier.color,
                        borderRadius: 5,
                        padding: "1px 6px",
                        fontWeight: 800,
                      }}
                    >
                      {t("leagueElo.currentBadge")}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: C.muted,
                    fontFamily: "monospace",
                  }}
                >
                  {t("leagueElo.minElo", tier.minElo)}
                </span>
              </div>
            );
          })}
        </div>
        <button
          onClick={onClose}
          style={{
            background: C.turf,
            color: "#050f0a",
            border: "none",
            borderRadius: 12,
            padding: "13px",
            fontSize: 14,
            fontWeight: 900,
            cursor: "pointer",
            marginTop: 4,
          }}
        >
          {t("leagueElo.close")}
        </button>
      </div>
    </div>
  );
}

// ── Carrousel des matchs de la semaine ──────────────────────────────────────
/** Initiales d'un nom d'équipe → monogramme (ex: "Red Storm FC" → "RS"). */
function initials(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return letters.slice(0, 2) || "?";
}

/** Libellé du coup d'envoi (null = match déjà joué). */
function kickoffLabel(scheduledAt: string, nowMs: number, t: (k: string, ...a: any[]) => string): string | null {
  const diff = new Date(scheduledAt).getTime() - nowMs;
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 24) {
    return (
      new Date(scheduledAt).toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
      }) + " · 16:00"
    );
  }
  return t("leagueOverview.startsIn", h, String(m).padStart(2, "0"));
}

function Monogram({ name, color }: { name: string; color: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          border: `2.5px solid ${color}`,
          display: "grid",
          placeItems: "center",
          fontSize: 21,
          fontWeight: 900,
          color,
          fontFamily: "monospace",
          letterSpacing: 1,
          boxShadow: `0 0 18px ${color}33`,
        }}
      >
        {initials(name)}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          textAlign: "center",
          color: C.text,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        {name}
      </div>
    </div>
  );
}

interface CarouselSlide {
  round: number;
  opponentName: string;
  scheduledAt: string;
  due: boolean;
  result: "win" | "draw" | "loss" | null;
  goalsFor: number | null;
  goalsAgainst: number | null;
}

function MatchCarousel({
  slides,
  myTeamName,
  matchesDone,
}: {
  slides: CarouselSlide[];
  myTeamName: string;
  matchesDone: number;
}) {
  const { t } = useI18n();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [active, setActive] = React.useState(() => {
    const idx = slides.findIndex((s) => !s.due);
    return idx === -1 ? Math.max(0, slides.length - 1) : idx;
  });
  const nowMs = Date.now();

  // Au montage, on se positionne sur le match courant (1er non joué).
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = active * el.clientWidth;
  }, []); // eslint-disable-line

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== active) setActive(idx);
  };

  return (
    <div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          borderRadius: 22,
        }}
      >
        {slides.map((s) => {
          const ko = kickoffLabel(s.scheduledAt, nowMs, t);
          return (
            <div
              key={s.round}
              style={{
                flex: "0 0 100%",
                scrollSnapAlign: "center",
                background: "linear-gradient(180deg,#10141d 0%,#0a0d13 60%,rgba(159,185,255,0.12) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 22,
                overflow: "hidden",
                boxShadow: "0 16px 48px -8px rgba(159,185,255,0.18)",
              }}
            >
              {/* Header du slide */}
              <div
                style={{
                  padding: "16px 20px",
                  background: "#1C2C42",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.2 }}>
                    {t("leagueOverview.roundLabel", s.round + 1)}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    {ko ?? t("leagueOverview.fullTime")}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 900,
                      lineHeight: 1,
                      fontFamily: "monospace",
                    }}
                  >
                    {matchesDone}
                    <span style={{ fontSize: 14, color: C.muted, fontWeight: 700 }}>
                      /{MATCHES_PER_WEEK}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    {t("leagueOverview.matchesPlayed")}
                  </div>
                </div>
              </div>

              {/* Corps : monogrammes + score/VS */}
              <div
                style={{
                  padding: "32px 16px 30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <Monogram name={myTeamName} color={C.turf} />

                <div style={{ textAlign: "center", flexShrink: 0, minWidth: 76 }}>
                  {s.due && s.goalsFor != null ? (
                    <>
                      <div
                        style={{
                          fontSize: 26,
                          fontWeight: 900,
                          fontFamily: "monospace",
                          letterSpacing: 1,
                          color:
                            s.result === "win"
                              ? C.turf
                              : s.result === "loss"
                              ? C.live
                              : C.gold,
                        }}
                      >
                        {s.goalsFor}–{s.goalsAgainst}
                      </div>
                      <div style={{ fontSize: 10, color: C.faint, marginTop: 4 }}>
                        {s.result === "win"
                          ? t("leagueOverview.win")
                          : s.result === "loss"
                          ? t("leagueOverview.loss")
                          : t("leagueOverview.draw")}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 900,
                          color: C.muted,
                          fontFamily: "monospace",
                          letterSpacing: 1,
                        }}
                      >
                        {t("leagueOverview.vs")}
                      </div>
                      <div style={{ fontSize: 10, color: C.faint, marginTop: 4 }}>
                        {new Date(s.scheduledAt).toLocaleDateString("en-US", {
                          weekday: "short",
                          day: "numeric",
                        })}{" "}
                        · 16:00
                      </div>
                    </>
                  )}
                </div>

                <Monogram name={s.opponentName} color={C.live} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Points du carrousel (style Instagram) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 7,
          marginTop: 14,
        }}
      >
        {slides.map((s, i) => (
          <span
            key={i}
            onClick={() => {
              const el = scrollRef.current;
              if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
            }}
            style={{
              width: i === active ? 9 : 7,
              height: i === active ? 9 : 7,
              borderRadius: 999,
              background:
                i === active ? C.text : "rgba(255,255,255,0.22)",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Settings modal ────────────────────────────────────────────────────────
function LeagueSettingsModal({
  teamName,
  onClose,
  onLeave,
}: {
  teamName: string;
  onClose: () => void;
  onLeave: () => void;
}) {
  const { t } = useI18n();
  const [confirming, setConfirming] = React.useState(false);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          borderRadius: 22,
          border: "1.5px solid rgba(255,255,255,0.08)",
          width: "100%",
          maxWidth: 360,
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 900 }}>{t("leagueSettings.title")}</div>

        {/* Current team */}
        <div
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            padding: "14px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>
            {t("leagueSettings.currentTeamLabel")}
          </div>
          <div style={{ fontSize: 16, fontWeight: 900, color: C.text }}>{teamName}</div>
        </div>

        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            style={{
              background: "rgba(239,68,68,0.12)",
              color: "#EF4444",
              border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 12,
              padding: "13px",
              fontSize: 14,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {t("leagueSettings.leaveTeam")}
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 13, color: C.muted, textAlign: "center" }}>
              {t("leagueSettings.leaveConfirmMsg")}
            </div>
            <button
              onClick={onLeave}
              style={{
                background: "#EF4444",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "13px",
                fontSize: 14,
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              {t("leagueSettings.confirmLeave")}
            </button>
            <button
              onClick={() => setConfirming(false)}
              style={{
                background: "transparent",
                color: C.muted,
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 12,
                padding: "11px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {t("leagueSettings.cancel")}
            </button>
          </div>
        )}

        {!confirming && (
          <button
            onClick={onClose}
            style={{
              background: C.turf,
              color: "#050f0a",
              border: "none",
              borderRadius: 12,
              padding: "13px",
              fontSize: 14,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {t("leagueSettings.close")}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main overview ──────────────────────────────────────────────────────────
export interface LeagueOverviewProps {
  tier: LeagueTier;
  nextTier?: LeagueTier | null;
  elo: number;
  daysLeft: number;
  matchesDone: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  schedule: ScheduleEntry[];
  matchPowers: Record<number, string>;
  preparedCount: number;
  leagueMembers: LeagueMember[];
  leagueGold: number;
  fieldAssets: FieldAsset[];
  teamCombos: ComboActivation[];
  standings: TeamStanding[];
  myRank: number;
  ready: boolean;
  onPrepare: () => void;
  onShowRoster: () => void;
  onLeave: () => void;
}

export function LeagueOverview(props: LeagueOverviewProps) {
  const {
    tier,
    elo,
    daysLeft,
    matchesDone,
    schedule,
    matchPowers,
    preparedCount,
    leagueMembers,
    leagueGold,
    fieldAssets,
    teamCombos,
    standings,
    myRank,
    ready,
    onPrepare,
    onShowRoster,
    onLeave,
  } = props;

  const { t } = useI18n();
  const [eloOpen, setEloOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [boardTab, setBoardTab] = React.useState<"standings" | "schedule">(
    "standings"
  );
  // Équipe sélectionnée depuis le classement → fiche détaillée.
  const [selectedTeam, setSelectedTeam] = React.useState<TeamStanding | null>(
    null
  );

  const topPlayers = [...leagueMembers].slice(0, 4);

  // Mon équipe dans le classement → ses fixtures = source du carrousel.
  const myStanding = standings.find((s) => s.isMe);
  const myTeamName = myStanding?.name ?? TEAM_NAME;
  const carouselSlides: CarouselSlide[] = React.useMemo(() => {
    const fx = myStanding?.fixtures ?? [];
    if (fx.length > 0) {
      return [...fx]
        .sort((a, b) => a.round - b.round)
        .map((f) => ({
          round: f.round,
          opponentName: f.opponentName,
          scheduledAt: f.scheduledAt,
          due: f.due,
          result: f.result,
          goalsFor: f.goalsFor,
          goalsAgainst: f.goalsAgainst,
        }));
    }
    // Fallback avant chargement : on dérive du calendrier brut.
    return schedule.map((s) => ({
      round: s.day,
      opponentName: s.opponentName,
      scheduledAt: s.scheduledAt,
      due: s.due,
      result: null,
      goalsFor: null,
      goalsAgainst: null,
    }));
  }, [myStanding, schedule]);

  // Fiche détaillée d'une équipe (ouverte depuis le classement).
  if (selectedTeam) {
    return (
      <LeagueTeamDetail
        team={selectedTeam}
        myMembers={leagueMembers}
        onBack={() => setSelectedTeam(null)}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {eloOpen && (
        <EloModal currentKey={tier.key} onClose={() => setEloOpen(false)} />
      )}

      {settingsOpen && (
        <LeagueSettingsModal
          teamName={myTeamName}
          onClose={() => setSettingsOpen(false)}
          onLeave={() => { setSettingsOpen(false); onLeave(); }}
        />
      )}

      {/* ── Titre + bouton infos équipe ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.2 }}>{t("leagueOverview.title")}</div>
          <button
            onClick={() => setSettingsOpen(true)}
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              display: "grid",
              placeItems: "center",
              fontSize: 14,
              cursor: "pointer",
              color: C.muted,
            }}
          >
            ⚙
          </button>
        </div>
        <button
          onClick={() => setEloOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: `${tier.color}18`,
            border: `1px solid ${tier.color}44`,
            borderRadius: 12,
            padding: "7px 12px",
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 16 }}>{tier.emoji}</span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: tier.color }}>{tier.label}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, fontFamily: "monospace" }}>{elo} ELO</span>
          <span style={{ fontSize: 13, color: C.muted }}>⋯</span>
        </button>
      </div>


      {/* ── Carrousel des matchs de la semaine (swipe) ── */}
      <MatchCarousel
        slides={carouselSlides}
        myTeamName={myTeamName}
        matchesDone={matchesDone}
      />

      {/* ── Bouton Préparer les matchs ── */}
      <button
        onClick={onPrepare}
        style={{
          width: "100%",
          background:
            "radial-gradient(ellipse at 50% 50%, #00FF6E 0%, #00CD60 100%)",
          color: "#050f0a",
          border: "none",
          borderRadius: 16,
          padding: "18px 24px",
          fontSize: 16,
          fontWeight: 900,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        {t("leagueOverview.prepareMatches")}{" "}
        <span style={{ color: "rgba(5,15,10,0.45)", fontWeight: 900 }}>
          {t("leagueOverview.preparedRatio", preparedCount, MATCHES_PER_WEEK)}
        </span>
      </button>

      {/* ── Atouts sur le terrain d'équipe (système TFT) ── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: "rgba(255,255,255,0.35)",
              letterSpacing: 2,
              textTransform: "uppercase",
            }}
          >
            {t("leagueOverview.assetsOnField")}
          </div>
          <span
            style={{ fontSize: 13, fontWeight: 900, color: "#7DD3FC" }}
            title="League Golds"
          >
            💰 {leagueGold}
          </span>
        </div>

        {/* Récap des synergies débloquées par l'équipe */}
        <div style={{ marginBottom: 8 }}>
          <ComboRecap combos={teamCombos} />
        </div>

        {/* Liste des atouts + émetteur en bout de ligne */}
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          {fieldAssets.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: C.muted,
                padding: "16px 14px",
                textAlign: "center",
              }}
            >
              {t("leagueOverview.noAssetsYet")}
            </div>
          ) : (
            fieldAssets.map((f, idx) => {
              const card = CARD_BY_ID[f.cardId];
              if (!card) return null;
              const fam = FAMILIES[primaryFamily(card)];
              return (
                <div
                  key={`${f.sender}-${idx}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 12px",
                    background: f.isMe ? "rgba(45,227,107,0.05)" : "transparent",
                    borderBottom:
                      idx !== fieldAssets.length - 1
                        ? "1px solid rgba(255,255,255,0.04)"
                        : "none",
                  }}
                >
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: `${fam.color}22`,
                      border: `1px solid ${fam.color}`,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 17,
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {card.name}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: f.isMe ? C.turf : C.muted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.isMe ? t("leagueOverview.meBadge") : f.sender}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Toggle Standings / Match Schedule ── */}
      <div
        style={{
          display: "flex",
          gap: 6,
          background: C.surface,
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 12,
          padding: 4,
        }}
      >
        {([
          ["standings", t("leagueOverview.standingsTab")],
          ["schedule", t("leagueOverview.scheduleTab")],
        ] as const).map(([key, label]) => {
          const active = boardTab === key;
          return (
            <button
              key={key}
              onClick={() => setBoardTab(key)}
              style={{
                flex: 1,
                background: active ? "rgba(45,227,107,0.12)" : "transparent",
                border: active
                  ? "1px solid rgba(45,227,107,0.4)"
                  : "1px solid transparent",
                borderRadius: 9,
                padding: "9px 0",
                fontSize: 12.5,
                fontWeight: 800,
                color: active ? C.turf : C.muted,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Calendrier de la semaine (lecture seule) ── */}
      {boardTab === "schedule" && (
      <div>
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          {schedule.map((m, i) => {
            const prepared = !!matchPowers[i];
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderBottom:
                    i !== schedule.length - 1
                      ? "1px solid rgba(255,255,255,0.04)"
                      : "none",
                  opacity: m.due ? 0.5 : 1,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: prepared ? C.turf : "rgba(255,255,255,0.15)",
                    boxShadow: prepared ? "0 0 6px rgba(45,227,107,0.7)" : "none",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {m.opponentName}
                </span>
                <span style={{ fontSize: 11, color: C.muted }}>
                  {new Date(m.scheduledAt).toLocaleDateString("en-US", {
                    weekday: "short",
                    day: "numeric",
                  })}
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: m.due ? C.faint : prepared ? C.turf : C.muted,
                    minWidth: 54,
                    textAlign: "right",
                  }}
                >
                  {m.due ? t("leagueOverview.played") : prepared ? t("leagueOverview.ready") : t("leagueOverview.prepare")}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* ── Classement des équipes ── */}
      {boardTab === "standings" && (
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "rgba(255,255,255,0.35)",
            letterSpacing: 2,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {t("leagueOverview.standingsTitle", ready ? myRank : undefined)}
        </div>
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          {/* En-tête colonnes */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 14px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.3)",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <span style={{ width: 18, textAlign: "center" }}>{t("leagueOverview.rankColumn")}</span>
            <span style={{ flex: 1 }}>{t("leagueOverview.teamColumn")}</span>
            <span style={{ width: 26, textAlign: "center" }}>{t("leagueOverview.playedColumn")}</span>
            <span style={{ width: 60, textAlign: "center" }}>{t("leagueOverview.wdlColumn")}</span>
            <span style={{ width: 34, textAlign: "right" }}>{t("leagueOverview.ptsColumn")}</span>
            <span style={{ width: 12 }} />
          </div>

          {!ready
            ? Array.from({ length: MATCHES_PER_WEEK + 1 }).map((_, idx) => (
                <SkeletonRow key={idx} last={idx === MATCHES_PER_WEEK} />
              ))
            : standings.map((s, idx) => {
                const rank = idx + 1;
                return (
                  <div
                    key={s.name}
                    onClick={() => setSelectedTeam(s)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "11px 14px",
                      cursor: "pointer",
                      background: s.isMe
                        ? "rgba(45,227,107,0.06)"
                        : "transparent",
                      borderBottom:
                        idx !== standings.length - 1
                          ? "1px solid rgba(255,255,255,0.04)"
                          : "none",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        width: 18,
                        textAlign: "center",
                        color: rank === 1 ? C.gold : "rgba(255,255,255,0.25)",
                      }}
                    >
                      {rank}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 13,
                        fontWeight: s.isMe ? 900 : 600,
                        color: s.isMe ? C.turf : C.text,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {s.name}
                      {s.isMe && (
                        <span
                          style={{
                            fontSize: 10,
                            marginLeft: 6,
                            background: "rgba(45,227,107,0.15)",
                            color: C.turf,
                            borderRadius: 5,
                            padding: "1px 5px",
                            fontWeight: 700,
                          }}
                        >
                          {t("leagueOverview.meBadgeInline")}
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        width: 26,
                        textAlign: "center",
                        fontSize: 12,
                        color: C.muted,
                      }}
                    >
                      {s.played}
                    </span>
                    <span
                      style={{
                        width: 60,
                        textAlign: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: "monospace",
                      }}
                    >
                      <span style={{ color: C.turf }}>{s.wins}</span>
                      <span style={{ color: "rgba(255,255,255,0.2)" }}>-</span>
                      <span style={{ color: C.gold }}>{s.draws}</span>
                      <span style={{ color: "rgba(255,255,255,0.2)" }}>-</span>
                      <span style={{ color: C.live }}>{s.losses}</span>
                    </span>
                    <span
                      style={{
                        width: 34,
                        textAlign: "right",
                        fontSize: 13,
                        fontWeight: 900,
                        color: C.text,
                      }}
                    >
                      {s.points}
                    </span>
                    <span
                      style={{
                        width: 12,
                        fontSize: 14,
                        fontWeight: 800,
                        color: s.isMe ? C.turf : "rgba(255,255,255,0.25)",
                        textAlign: "right",
                      }}
                    >
                      ›
                    </span>
                  </div>
                );
              })}
        </div>
      </div>
      )}

    </div>
  );
}
